import { inject, Injectable, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { JointListItemDto } from '@therapist/shared/models/diagnosis/joint-list-item.dto';
import { JointApiService } from '@therapist/shared/services/api/joint-api.service';

@Injectable({ providedIn: 'root' })
export class JointStateService {
  readonly #jointApSerivce = inject(JointApiService);

  // All available diagnoses
  #allJoints = signal<JointListItemDto[]>([]);

  // User-selected diagnoses
  #selectedJoints = signal<JointListItemDto[]>([]);

  constructor() {
    if (environment.enableStateLogging) {
      window.setInterval(() => console.log(this.#selectedJoints()), 2500);
    }
  }

  /** Loads all joints from API into state */
  loadAllJoints(): void {
    this.#jointApSerivce
      .getAll()
      .subscribe((data) => this.#allJoints.set(data));
  }

  /** Returns signal of all joints */
  getAllJoints(): Signal<JointListItemDto[]> {
    return this.#allJoints.asReadonly();
  }

  /** Adds a joint to the selection */
  addSelectedJoint(joint: JointListItemDto): void {
    this.#selectedJoints.set([
      ...this.#selectedJoints(),
      joint
    ]);
  }

  /** Adds a list of joints to the selection */
  addSelectedJointList(joints: JointListItemDto[]): void {
    joints.forEach(j => this.addSelectedJoint(j));
  }

  /** Removes all joints from the selection */
  removeAllSelectedJoints(): void {
    this.#selectedJoints.set([]);
  }

  /** Removes a joint from the selection */
  removeSelectedJoints(joint: JointListItemDto): void {
    this.#selectedJoints.set(
      this.#selectedJoints().filter(d => d.id !== joint.id)
    );
  }

  /** Removes all joints from the state */
  removeAll(): void {
    this.removeAllSelectedJoints();
    this.#allJoints.set([]);
  }

  /** Returns signal of selected joints */
  getSelectedJoints(): Signal<JointListItemDto[]> {
    return this.#selectedJoints.asReadonly();
  }

  /** Returns a comma-separated list of selected joint titles */
  getSelectedJointNames(): Signal<string | undefined> {
    return toSignal(of(this.#selectedJoints().map(d => d.type).join(', ')));
  }
}

